import { changeIndex, getAppReturnNodes, getRelevantNodes } from '@/util/array';
import List from '@/components/common/List';
import ErrorNodeWrapper from '@/components/validation/ErrorNodeWrapper';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { ReturnType, Subroutine } from '@/types/subroutine';
import { Variable } from '@/types/variable';
import { findEntrySubroutine } from '@/util/blocks';
import { getHighestNumber } from '@/util/number';
import { validateMemberType } from '@/util/validation/subroutines';
import { Button, Card } from '@mui/material';
import { FC } from 'react';
import TypeSelect from './TypeSelect';

type ReturnTypesProps = {
  subroutine?: Subroutine;
  updateApp: () => void;
};

const ReturnTypes: FC<ReturnTypesProps> = ({ updateApp, subroutine }) => {
  const { application } = useApplication();
  const { metadata } = useMetadata();

  // The lists bound to these return types; every appreturnvalues list in the app when this is the entry subroutine.
  const dependentNodes = (subroutineId: number) => {
    const nodes = getRelevantNodes(application, metadata, subroutineId, ['returnvalues', 'returnvariables']);
    const entry = findEntrySubroutine(application.subroutines, application.blocks.nodes, metadata);
    return entry?.id === subroutineId ? [...nodes, ...getAppReturnNodes(application, metadata)] : nodes;
  };

  const onArrowClick = (data: ReturnType[], index: number, arrowType: string) => {
    if (!subroutine?.return_types) return;

    const nodes = dependentNodes(subroutine.id);
    nodes.forEach((node) => {
      if (application?.blocks.details[node.node][node.value])
        application.blocks.details[node.node][node.value] = changeIndex(
          application.blocks.details[node.node][node.value] as [],
          index,
          arrowType === 'up' ? index - 1 : index + 1
        );
    });

    subroutine.return_types = data;
    updateApp();
  };

  return (
    <Card variant='outlined' sx={{ p: 1 }}>
      <List
        subheader='Return types'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          alignItems: 'start'
        }}
      >
        {subroutine?.return_types.map((returnType, index) => (
          <ErrorNodeWrapper
            key={`subroutine.return_types.${returnType.id}`}
            errors={validateMemberType(subroutine, 'return type', returnType, index)}
          >
            <TypeSelect
              value={returnType}
              data={subroutine.return_types}
              type='returnvalue'
              index={index}
              onArrowClick={(data, arrowType) => onArrowClick(data, index, arrowType)}
              onChange={(newReturnType) => {
                subroutine.return_types[index] = newReturnType;
                updateApp();
              }}
              onDelete={() => {
                const nodes = dependentNodes(subroutine.id);

                // Find and remove the block details value that matches with the removed return type
                nodes.forEach((node) => {
                  if (application?.blocks.details[node.node][node.value]) {
                    const result = application.blocks.details[node.node][node.value] as (Variable | undefined)[];
                    result.splice(index, 1);
                  }
                });

                subroutine.return_types = subroutine.return_types.filter((_, i) => i !== index);
                updateApp();
              }}
            />
          </ErrorNodeWrapper>
        ))}
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            if (!subroutine) return;

            const nodes = dependentNodes(subroutine.id);
            nodes.forEach((node) => {
              const nodeResults = application?.blocks.details[node.node][node.value] as unknown[];
              nodeResults && nodeResults.push(null);
            });

            const newReturnTypeId = getHighestNumber(subroutine.return_types, 'id') + 1;
            subroutine.return_types = [...subroutine.return_types, { id: newReturnTypeId, type: '', label: '' }];
            updateApp();
          }}
        >
          Add return type
        </Button>
      </List>
    </Card>
  );
};

export default ReturnTypes;
